import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../components/layouts/AppLayout'
import {
  Bell,
  Check,
  CheckCircle,
  Package,
  Money,
  Truck,
  X,
  ChatCircle,
  CaretLeft,
  CaretRight,
  Spinner,
  FunnelSimple,
  ArrowLeft
} from '@phosphor-icons/react'
import { notificationService, Notification } from '../services/notifications'
import { useAuth } from '../context/AuthContext'
import { useProfile } from '../context/ProfileContext'

const NOTIFICATION_ICONS: Record<string, typeof Bell> = {
  new_quote: Package,
  new_offer: Money,
  offer_accepted: CheckCircle,
  offer_declined: X,
  order_update: Truck,
  new_chat_message: ChatCircle,
}

const NOTIFICATION_COLORS: Record<string, string> = {
  new_quote: 'bg-blue-100 text-blue-600',
  new_offer: 'bg-emerald-100 text-emerald-600',
  offer_accepted: 'bg-green-100 text-green-600',
  offer_declined: 'bg-red-100 text-red-600',
  order_update: 'bg-orange-100 text-orange-600',
  new_chat_message: 'bg-purple-100 text-purple-600',
}

const PAGE_SIZE = 20

export default function NotificationsPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { currentProfile } = useProfile()

  // State
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [unreadCount, setUnreadCount] = useState(0)
  const [page, setPage] = useState(1)
  const [filterUnread, setFilterUnread] = useState(false)
  const [markingAll, setMarkingAll] = useState(false)

  const totalPages = Math.ceil(total / PAGE_SIZE)

  useEffect(() => {
    fetchNotifications()
  }, [page, filterUnread])

  async function fetchNotifications() {
    try {
      setLoading(true)
      const offset = (page - 1) * PAGE_SIZE
      const response = await notificationService.getNotifications(PAGE_SIZE, offset, filterUnread)
      setNotifications(response.notifications)
      setTotal(response.total)
      setUnreadCount(response.unreadCount)
    } catch (error) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleMarkAsRead(id: string) {
    try {
      await notificationService.markAsRead(id)
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, read: true } : n))
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (error) {
      console.error('Error marking as read:', error)
    }
  }

  async function handleMarkAllAsRead() {
    try {
      setMarkingAll(true)
      await notificationService.markAllAsRead()
      setNotifications(prev => prev.map(n => ({ ...n, read: true })))
      setUnreadCount(0)
    } catch (error) {
      console.error('Error marking all as read:', error)
    } finally {
      setMarkingAll(false)
    }
  }

  function handleNotificationClick(notification: Notification) {
    // Mark as read
    if (!notification.read) {
      handleMarkAsRead(notification.id)
    }

    // Navigate based on notification type and current profile
    const basePath = currentProfile === 'freight-forwarder' ? '/freight-forwarder' : '/shipper'

    switch (notification.type) {
      case 'new_quote':
        navigate(`/freight-forwarder/opportunities/${notification.reference_id}`)
        break
      case 'new_offer':
        navigate('/shipper/quotes')
        break
      case 'offer_accepted':
        if (currentProfile === 'freight-forwarder') {
          navigate('/freight-forwarder/my-offers')
        }
        break
      case 'offer_declined':
        if (currentProfile === 'freight-forwarder') {
          navigate('/freight-forwarder/opportunities')
        }
        break
      case 'order_update':
        navigate(`${basePath}/orders/${notification.reference_id}`)
        break
      case 'new_chat_message':
        // Parse reference_id which can be "orderId" or "orderId:messageId"
        const parts = notification.reference_id.split(':')
        const orderId = parts[0]
        const messageId = parts[1]
        if (messageId) {
          navigate(`${basePath}/orders/${orderId}?tab=chat&messageId=${messageId}`)
        } else {
          navigate(`${basePath}/orders/${orderId}?tab=chat`)
        }
        break
    }
  }

  function formatTime(dateString: string) {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  function formatRelativeTime(dateString: string) {
    const date = new Date(dateString)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    const hours = Math.floor(diff / 3600000)
    const days = Math.floor(diff / 86400000)

    if (minutes < 1) return 'Agora'
    if (minutes < 60) return `${minutes} min atras`
    if (hours < 24) return `${hours}h atras`
    if (days < 7) return `${days} dias atras`
    return formatTime(dateString)
  }

  function getBackPath() {
    return currentProfile === 'freight-forwarder' ? '/freight-forwarder/dashboard' : '/shipper/dashboard'
  }

  if (loading && notifications.length === 0) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center py-24">
          <Spinner size={32} className="animate-spin text-emerald-600" />
          <span className="ml-3 text-gray-500">Carregando notificacoes...</span>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(getBackPath())}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Notificacoes</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  {unreadCount > 0 ? (
                    <>{unreadCount} nao lidas de {total} total</>
                  ) : (
                    <>Todas as {total} notificacoes lidas</>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={markingAll}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  {markingAll ? (
                    <Spinner size={16} className="animate-spin" />
                  ) : (
                    <Check size={16} weight="bold" />
                  )}
                  Marcar todas como lidas
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filter Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FunnelSimple size={18} className="text-gray-400" />
              <span className="text-sm text-gray-600">Filtrar:</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setFilterUnread(false)
                  setPage(1)
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  !filterUnread
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => {
                  setFilterUnread(true)
                  setPage(1)
                }}
                className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                  filterUnread
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Nao lidas
                {unreadCount > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                    {unreadCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg border border-gray-200">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size={24} className="animate-spin text-emerald-600" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-16">
              <Bell size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-1">
                {filterUnread ? 'Nenhuma notificacao nao lida' : 'Nenhuma notificacao'}
              </p>
              <p className="text-sm text-gray-400">
                {filterUnread
                  ? 'Todas as notificacoes foram lidas'
                  : 'Voce nao tem notificacoes ainda'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => {
                const Icon = NOTIFICATION_ICONS[notification.type] || Bell
                const colorClass = NOTIFICATION_COLORS[notification.type] || 'bg-gray-100 text-gray-600'

                return (
                  <div
                    key={notification.id}
                    className={`flex gap-4 p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                      !notification.read ? 'bg-blue-50/50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {/* Icon */}
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                      <Icon size={24} weight="fill" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className={`font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </p>
                        <span className="text-xs text-gray-400 flex-shrink-0">
                          {formatRelativeTime(notification.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatTime(notification.created_at)}
                      </p>
                    </div>

                    {/* Unread Indicator / Action */}
                    <div className="flex items-start gap-2 flex-shrink-0">
                      {!notification.read ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            handleMarkAsRead(notification.id)
                          }}
                          className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                          title="Marcar como lida"
                        >
                          <Check size={18} />
                        </button>
                      ) : (
                        <div className="p-2 text-emerald-500">
                          <CheckCircle size={18} weight="fill" />
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Footer with count and pagination */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-100 px-4 py-3 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                {totalPages > 1 ? (
                  <>
                    Exibindo <span className="font-medium">{(page - 1) * PAGE_SIZE + 1}</span> a{' '}
                    <span className="font-medium">{Math.min(page * PAGE_SIZE, total)}</span> de{' '}
                    <span className="font-medium">{total}</span> notificacoes
                  </>
                ) : (
                  <>
                    <span className="font-medium">{total}</span> notificacao{total !== 1 ? 'es' : ''} no total
                  </>
                )}
              </p>
              {totalPages > 1 && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CaretLeft size={18} />
                  </button>
                  {/* Page numbers */}
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number
                    if (totalPages <= 5) {
                      pageNum = i + 1
                    } else if (page <= 3) {
                      pageNum = i + 1
                    } else if (page >= totalPages - 2) {
                      pageNum = totalPages - 4 + i
                    } else {
                      pageNum = page - 2 + i
                    }
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 text-sm font-medium rounded-lg transition-colors ${
                          page === pageNum
                            ? 'bg-emerald-600 text-white'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <CaretRight size={18} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </AppLayout>
  )
}
